import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { orderAPI } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Package,
  Clock,
  CheckCircle2,
  XCircle,
  ChefHat,
  ArrowLeft
} from 'lucide-react';
import BuyerLayout from '../../components/layouts/BuyerLayout';

const STATUS_CONFIG = {
  pending: { label: 'Pending', icon: Clock, className: 'status-pending' },
  confirmed: { label: 'Confirmed', icon: CheckCircle2, className: 'status-confirmed' },
  preparing: { label: 'Preparing', icon: ChefHat, className: 'status-preparing' },
  ready: { label: 'Ready', icon: Package, className: 'status-ready' },
  delivered: { label: 'Delivered', icon: CheckCircle2, className: 'status-delivered' },
  cancelled: { label: 'Cancelled', icon: XCircle, className: 'status-cancelled' },
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async () => {
    try {
      const response = await orderAPI.getAll(user.id, 'buyer');
      setOrders(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  return (
    <BuyerLayout>
      <div className="space-y-6 pb-mobile-nav">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button 
            onClick={() => navigate('/buyer')}
            className="p-2 hover:bg-muted rounded-full transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-2xl font-bold">My Orders</h1>
            <p className="text-sm text-muted-foreground">{orders.length} orders</p>
          </div>
        </div>

        {/* Orders List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="h-4 w-1/3 mb-2" />
                  <Skeleton className="h-3 w-1/2 mb-4" />
                  <Skeleton className="h-16 w-full" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : orders.length === 0 ? (
          <div className="empty-state py-20">
            <Package className="empty-state-icon" />
            <h3 className="font-heading text-xl font-semibold mb-2">No orders yet</h3>
            <p className="text-muted-foreground">Your order history will appear here</p>
          </div>
        ) : (
          <div className="space-y-4">
            {orders.map((order) => {
              const statusConfig = STATUS_CONFIG[order.status] || STATUS_CONFIG.pending;
              const StatusIcon = statusConfig.icon;
              
              return (
                <Card key={order.id} className="overflow-hidden" data-testid={`order-${order.id}`}>
                  <CardContent className="p-4">
                    {/* Order Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="font-mono text-sm text-muted-foreground">
                          #{order.id.slice(0, 8).toUpperCase()}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {format(new Date(order.created_at), 'dd MMM yyyy, hh:mm a')}
                        </p>
                      </div>
                      <Badge className={`${statusConfig.className} border`}>
                        <StatusIcon className="w-3 h-3 mr-1" />
                        {statusConfig.label}
                      </Badge>
                    </div>

                    {/* Order Items */}
                    <div className="bg-muted/50 rounded-xl p-3 mb-3">
                      {order.items.map((item, idx) => (
                        <div key={idx} className="flex justify-between text-sm py-1">
                          <span>{item.quantity}x {item.item_name}</span>
                          <span className="text-muted-foreground">
                            {item.price === 0 ? 'Free' : `₹${item.price * item.quantity}`}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Order Footer */}
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-muted-foreground">
                        {order.items.reduce((sum, item) => sum + item.quantity, 0)} items
                      </p>
                      <p className="font-semibold text-primary">
                        Total: ₹{order.total_amount.toFixed(2)}
                      </p>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </BuyerLayout>
  );
}
